import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { api } from '../../../lib/api-client';
import {
  WS_EVENTS,
  type AISuggestionBlockedByConflictPayload,
  type AISuggestionErrorPayload,
  type AISuggestionGeneratingPayload,
  type AISuggestionReadyPayload,
  type AISuggestionWarning,
  type SuggestionChip,
} from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

export type AISuggestionDecisionAction =
  | 'accepted'
  | 'edited'
  | 'rejected'
  | 'ignored'
  | 'regenerated';

export interface ActiveSuggestion {
  readonly suggestionId: string;
  readonly proposedText: string;
  readonly agentId: string;
  readonly agentVersion: string;
  readonly latencyMs: number;
  readonly receivedAt: number;
  readonly expiresAt: number;
  /**
   * PR-B Fatia 7 — Solo/Team/Pro com ConflictAlert ativo no bond da
   * conversa. Frontend renderiza banner discreto sobre o Composer.
   */
  readonly warning?: AISuggestionWarning;
  /**
   * Fase 1 «um cérebro só» — chips de conduta que o dry-run do conductor
   * sugeriu no turno (o que a IA FARIA e o operador aprova). Ausente na
   * sugestão legado v1 (retrocompat).
   */
  readonly chips?: SuggestionChip[];
}

export interface BlockedByConflictState {
  readonly alertId: string;
  readonly alertCategory: string;
  readonly alertSeverity: string;
  readonly blockedAt: number;
}

export type SuggestionStatus = 'idle' | 'loading' | 'ready' | 'error' | 'blocked';

/**
 * useAISuggestion — Fatia 7 (modo H_a + sugestão IA inline).
 *
 * Subscreve `ai.suggestion.ready` + `ai.suggestion.error` para a conversa em
 * foco. Mantém estado da "sugestão atual" e expõe ações para o Composer
 * registrar decisão do atendente.
 *
 * Cancelamento implícito: ao receber `ai.suggestion.ready` nova para a mesma
 * conversa, a sugestão anterior é substituída (Engine já marcou a anterior
 * como `superseded` antes de gerar nova — ADR-134).
 *
 * Task 2 (Fatia 2b) — `requestManual`: solicita geração sob demanda via
 * `POST /v1/ai-suggestions/conversations/:id/generate`. Otimista: acende
 * `loading` antes do await; em erro 409/outro → `error` + `errorMessage`.
 * A sugestão em si chega via WS `AI_SUGGESTION_READY` (não pelo retorno do POST).
 */
export function useAISuggestion(conversationId: string | undefined): {
  status: SuggestionStatus;
  suggestion: ActiveSuggestion | null;
  errorReason: string | null;
  /**
   * Task 2 — mensagem legível quando `status='error'` por falha do `requestManual`
   * (ex.: 409 guard falhou). Distinto de `errorReason` (que vem do WS error event).
   * Resetado por `clear()` e por nova chamada de `requestManual`.
   */
  errorMessage: string | null;
  blocked: BlockedByConflictState | null;
  /** Limpa a sugestão localmente (sem chamar API — usado por digitar-por-cima ou expiração). */
  clear: () => void;
  /** Registra decisão no Engine + limpa estado local. */
  recordDecision: (action: AISuggestionDecisionAction, finalText?: string) => Promise<void>;
  /**
   * Task 2 — solicita geração de sugestão IA sob demanda (modo H_a).
   * Optimista: status vira 'loading' antes do await. Retorno do endpoint é
   * ignorado — a sugestão chega via WS `AI_SUGGESTION_READY`.
   * Em erro (409 guard / rede): status='error' + errorMessage preenchido.
   */
  requestManual: () => Promise<void>;
} {
  const [status, setStatus] = useState<SuggestionStatus>('idle');
  const [suggestion, setSuggestion] = useState<ActiveSuggestion | null>(null);
  const [errorReason, setErrorReason] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<BlockedByConflictState | null>(null);
  const { subscribe } = useWebSocket();
  const queryClient = useQueryClient();

  // Task 57 — REST initial fetch canonical para popular state quando
  // componente monta APÓS suggestion ter sido gerada (caso WS event
  // missed por timing de mount/reconnect). Pattern hybrid REST+WS mirror
  // de `useLivingSummary` + `useQualitySignals`.
  const activeQuery = useQuery({
    queryKey: ['ai-suggestion-active', conversationId],
    enabled: conversationId !== undefined,
    queryFn: () => {
      if (conversationId === undefined) return Promise.resolve(null);
      return api.getActiveAISuggestion(conversationId);
    },
    // Não refetcha automaticamente — WS event é a fonte canônica de updates.
    // REST fetch só serve para popular state inicial no mount.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Quando REST traz suggestion ativa AND state ainda é idle (WS não
  // populou estado), aplica canonical. Se WS já populou (status='ready'
  // OR outros), preserva o estado WS (mais recente).
  useEffect(() => {
    const data = activeQuery.data;
    if (data === undefined || data === null) return;
    if (status !== 'idle') return;
    setSuggestion({
      suggestionId: data.suggestionId,
      proposedText: data.proposedText,
      agentId: data.agentId,
      agentVersion: data.agentVersion,
      latencyMs: 0,
      receivedAt: new Date(data.createdAt).getTime(),
      expiresAt: new Date(data.expiresAt).getTime(),
      ...(data.chips !== undefined && { chips: data.chips }),
    });
    setStatus('ready');
  }, [activeQuery.data, status]);

  useEffect(() => {
    if (conversationId === undefined) return;

    // Item 287 (FE-2) — geração em curso: acende `loading` (GeneratingStrip +
    // textarea readonly). `ready`/`error`/`blocked` subsequentes sobrescrevem.
    const unsubGenerating = subscribe<AISuggestionGeneratingPayload>(
      WS_EVENTS.AI_SUGGESTION_GENERATING,
      (envelope) => {
        const { data } = envelope;
        if (data.conversationId !== conversationId) return;
        setSuggestion(null);
        setErrorReason(null);
        setBlocked(null);
        setStatus('loading');
      },
    );

    const unsubReady = subscribe<AISuggestionReadyPayload>(
      WS_EVENTS.AI_SUGGESTION_READY,
      (envelope) => {
        const { data } = envelope;
        if (data.conversationId !== conversationId) return;
        setSuggestion({
          suggestionId: data.suggestionId,
          proposedText: data.proposedText,
          agentId: data.agentId,
          agentVersion: data.agentVersion,
          latencyMs: data.latencyMs,
          receivedAt: Date.now(),
          expiresAt: new Date(data.expiresAt).getTime(),
          ...(data.warning !== undefined && { warning: data.warning }),
          ...(data.chips !== undefined && { chips: data.chips }),
        });
        setStatus('ready');
        setErrorReason(null);
        setBlocked(null);
      },
    );

    const unsubError = subscribe<AISuggestionErrorPayload>(
      WS_EVENTS.AI_SUGGESTION_ERROR,
      (envelope) => {
        const { data } = envelope;
        if (data.conversationId !== conversationId) return;
        setSuggestion(null);
        setErrorReason(data.reason);
        setStatus('error');
        setBlocked(null);
      },
    );

    const unsubBlocked = subscribe<AISuggestionBlockedByConflictPayload>(
      WS_EVENTS.AI_SUGGESTION_BLOCKED_BY_CONFLICT,
      (envelope) => {
        const { data } = envelope;
        if (data.conversationId !== conversationId) return;
        setSuggestion(null);
        setErrorReason(null);
        setBlocked({
          alertId: data.alertId,
          alertCategory: data.alertCategory,
          alertSeverity: data.alertSeverity,
          blockedAt: new Date(data.blockedAt).getTime(),
        });
        setStatus('blocked');
      },
    );

    return () => {
      unsubGenerating();
      unsubReady();
      unsubError();
      unsubBlocked();
    };
  }, [conversationId, subscribe]);

  const clear = useCallback(() => {
    setSuggestion(null);
    setStatus('idle');
    setErrorReason(null);
    setErrorMessage(null);
    setBlocked(null);
    // Limpa o cache do REST inicial: sem isso, o effect de hidratação
    // (deps [activeQuery.data, status]) re-aplica a sugestão ao voltar a 'idle'.
    if (conversationId !== undefined) {
      queryClient.setQueryData(['ai-suggestion-active', conversationId], null);
    }
  }, [conversationId, queryClient]);

  const recordDecision = useCallback(
    async (action: AISuggestionDecisionAction, finalText?: string): Promise<void> => {
      if (!suggestion) return;
      try {
        await api.recordSuggestionDecision(suggestion.suggestionId, {
          action,
          ...(finalText !== undefined && { finalText }),
        });
      } finally {
        setSuggestion(null);
        setStatus('idle');
        // Evita re-hidratação da sugestão consumida pelo effect inicial REST.
        if (conversationId !== undefined) {
          queryClient.setQueryData(['ai-suggestion-active', conversationId], null);
        }
      }
    },
    [suggestion, conversationId, queryClient],
  );

  /**
   * Task 2 — solicita geração sob demanda via POST .../generate.
   *
   * Otimista: `status` vira `loading` antes do await (o WS `GENERATING`
   * também faz isso, mas pode chegar depois — garantimos UI responsiva).
   * O retorno do endpoint é irrelevante para o estado: a sugestão chega via
   * WS `AI_SUGGESTION_READY`. Em erro → `status='error'` + `errorMessage`.
   */
  const requestManual = useCallback(async (): Promise<void> => {
    if (conversationId === undefined) return;
    // Optimistic: acende loading imediatamente.
    setStatus('loading');
    setErrorMessage(null);
    try {
      const res = await api.generateAISuggestion(conversationId);
      // Item 482 — o engine diz POR QUÊ não sugeriu (sem conflito). Antes o `null` ficava
      // em `loading` até o WS de erro chegar e virar «IA indisp.» — a IA não estava
      // indisponível. Engine antigo (sem `reason`) mantém o comportamento: loading até o WS.
      if (
        res.suggestionId === null &&
        !res.blockedByConflict &&
        typeof res.reason === 'string' &&
        res.reason.length > 0
      ) {
        setErrorMessage(res.reason);
        setStatus('error');
        return;
      }
      // Sucesso: mantém loading até WS READY chegar.
    } catch (err) {
      const msg =
        err instanceof Error && err.message.length > 0
          ? err.message
          : 'Não foi possível solicitar sugestão da IA.';
      setErrorMessage(msg);
      setStatus('error');
    }
  }, [conversationId]);

  return {
    status,
    suggestion,
    errorReason,
    errorMessage,
    blocked,
    clear,
    recordDecision,
    requestManual,
  };
}
